<template>
  <div class="ufb-modal">
    <!-- Navy side rail -->
    <aside class="ufb-side">
      <div class="ufb-side-top">
        <div class="ufb-coral-bar"></div>
        <div class="ufb-eyebrow">Feedback</div>
        <h3 class="ufb-side-title">Help us build a better RPD.</h3>
        <p class="ufb-side-text">
          Every report is read by the product team within one business day.
        </p>

        <div class="ufb-side-list">
          <div class="ufb-side-item">
            <font-awesome-icon
              :icon="['fas', 'check']"
              class="ufb-side-check"
            />
            <span>You'll get a confirmation email with a ticket number.</span>
          </div>
          <div class="ufb-side-item">
            <font-awesome-icon
              :icon="['fas', 'check']"
              class="ufb-side-check"
            />
            <span
              >We attach your current page, session, and browser
              automatically.</span
            >
          </div>
          <div class="ufb-side-item">
            <font-awesome-icon
              :icon="['fas', 'check']"
              class="ufb-side-check"
            />
            <span>Urgent data problems? Mark as <em>Data issue</em>.</span>
          </div>
        </div>
      </div>

      <div class="ufb-side-foot">
        <font-awesome-icon :icon="['fas', 'envelope']" />
        <span>info@realpropertydata.ca</span>
      </div>
    </aside>

    <!-- Right pane -->
    <section class="ufb-main">
      <button
        type="button"
        class="ufb-close"
        aria-label="Close"
        @click="closeModal"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>

      <div class="ufb-body">
        <div class="ufb-field">
          <label class="ufb-label">Category</label>
          <div class="ufb-cats">
            <button
              v-for="c in categories"
              :key="c.k"
              type="button"
              :class="[
                'ufb-cat',
                { 'ufb-cat--active': formData.category === c.k },
              ]"
              @click="formData.category = c.k"
            >
              <span class="ufb-cat-head">
                <font-awesome-icon
                  :icon="['fas', c.icon]"
                  :class="[
                    'ufb-cat-icon',
                    { 'ufb-cat-icon--active': formData.category === c.k },
                  ]"
                />
                {{ c.label }}
              </span>
              <span class="ufb-cat-sub">{{ c.sub }}</span>
            </button>
          </div>
        </div>

        <div class="ufb-row-2">
          <div class="ufb-field">
            <label class="ufb-label">Name</label>
            <input
              class="ufb-input"
              type="text"
              v-model="formData.name"
              placeholder="Jane Patel"
            />
          </div>
          <div class="ufb-field">
            <label class="ufb-label">Email</label>
            <input
              class="ufb-input"
              type="email"
              v-model="formData.email"
              placeholder="jane@brokerage.com"
            />
          </div>
        </div>

        <div class="ufb-field">
          <label class="ufb-label">
            What happened?<span class="ufb-required">*</span>
          </label>
          <textarea
            class="ufb-textarea"
            v-model="formData.content"
            rows="4"
            placeholder="The cap-rate on 1421 Oak Ave shows 4.75% in the table but 3.20% on the detail page…"
            @paste="handlePaste"
          ></textarea>
        </div>

        <label
          class="ufb-drop"
          :class="{ 'ufb-drop--hover': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            class="ufb-file-input"
            multiple
            accept="image/*"
            @change="onFilePicker"
          />
          <span class="ufb-drop-left">
            <font-awesome-icon
              :icon="['fas', 'paperclip']"
              class="ufb-drop-clip"
            />
            <span><strong>Add screenshots</strong> · drop or paste here</span>
          </span>
          <span class="ufb-drop-right">PNG, JPG · 10MB</span>
        </label>

        <div v-if="thumbs.length" class="ufb-thumbs">
          <div v-for="(img, i) in thumbs" :key="i" class="ufb-thumb">
            <img :src="img.src" alt="Screenshot" />
            <button
              type="button"
              class="ufb-thumb-x"
              aria-label="Remove"
              @click="deleteScreenshot(i)"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
        </div>
      </div>

      <footer class="ufb-footer">
        <button
          type="button"
          class="ufb-btn ufb-btn--ghost"
          @click="closeModal"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ufb-btn ufb-btn--primary"
          @click="onSubmit"
        >
          Send feedback
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { BACKEND_URL } from "../../variables";
import { useUserStore } from "../../store/UserStore";
import { mapState } from "pinia";

const DEFAULT_CATEGORY = "idea";

export default {
  name: "UserFeedback",

  props: {
    formData: { type: Object, required: true },
  },

  data() {
    return {
      userStore: useUserStore(),
      isDragging: false,
      categories: [
        {
          k: "bug",
          label: "Bug",
          icon: "bug",
          sub: "Something is broken or wrong",
        },
        {
          k: "idea",
          label: "Idea",
          icon: "lightbulb",
          sub: "A feature or improvement",
        },
        {
          k: "data",
          label: "Data issue",
          icon: "database",
          sub: "Bad price, listing, or comp",
        },
      ],
    };
  },

  computed: {
    ...mapState(useUserStore, ["current_user"]),
    thumbs() {
      return Array.isArray(this.formData.files) ? this.formData.files : [];
    },
  },

  created() {
    if (!this.formData.category) {
      this.$set(this.formData, "category", DEFAULT_CATEGORY);
    }
    if (!this.formData.files) {
      this.$set(this.formData, "files", []);
    }
    if (!this.formData.url && typeof window !== "undefined") {
      this.$set(this.formData, "url", window.location.href);
    }
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("feedback-modal");
      this.$emit("hideFeedbackModal");
    },

    pushFile(file) {
      if (!file || !file.type || file.type.indexOf("image") === -1) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.formData.files.push({
          src: e.target.result,
          name: `screenshot_${Date.now()}.jpg`,
        });
      };
      reader.readAsDataURL(file);
    },

    onFilePicker(event) {
      const files = event.target.files;
      if (!files || !files.length) return;
      Array.from(files).forEach((f) => this.pushFile(f));
      event.target.value = "";
    },

    handleDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;
      Array.from(files).forEach((f) => this.pushFile(f));
    },

    handlePaste(event) {
      const items = event.clipboardData && event.clipboardData.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          this.pushFile(items[i].getAsFile());
        }
      }
    },

    deleteScreenshot(index) {
      this.formData.files.splice(index, 1);
    },

    resetForm() {
      this.formData.name = "";
      this.formData.email = "";
      this.formData.subject = "";
      this.formData.contact = "";
      this.formData.content = "";
      this.formData.url = "";
      this.formData.files = [];
      this.formData.category = DEFAULT_CATEGORY;
    },

    onSubmit() {
      if (!this.formData.content || this.formData.content.trim() === "") {
        this.$bvToast.toast("Please describe what happened.", {
          title: "Missing details",
          variant: "warning",
          autoHideDelay: 4000,
        });
        return;
      }

      try {
        $.post(
          BACKEND_URL + "api/feedback",
          { message: this.formData },
          (data) => {
            if (data.status) {
              this.resetForm();
              this.$bvToast.toast("Feedback sent successfully!", {
                title: "Success!",
                variant: "success",
                autoHideDelay: 5000,
              });
              this.$emit("hideFeedbackModal");
            }
          }
        );
      } catch (error) {
        this.resetForm();
        this.$bvToast.toast("Error sending feedback. Please try again later.", {
          title: "Failed",
          variant: "warning",
          autoHideDelay: 5000,
        });
        this.$emit("hideFeedbackModal");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$navy: #12283f;
$navy-hover: #1b3552;
$dark-violet: #14134f;
$blue-violet: #343376;
$sky-blue: #edf2fe;
$coral: #fc5868;
$light-grey: #e9e8e9;
$dark-grey: #545454;
$fg-3: #8a8a8a;
$border: $light-grey;
$border-strong: #c9ccd2;
$divider: #eef0f4;
$surface-sunken: #f7f8fb;
$bg-subtle: $sky-blue;

$font-display: "Poppins", "Helvetica Light", "Helvetica", Arial, sans-serif;
$font-body: "Helvetica Light", "Helvetica", "Helvetica Neue", Arial, sans-serif;

.ufb-modal {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 760px;
  margin: 0 0;
  background: #fff;
  overflow: hidden;
  font-family: $font-body;
  color: $navy;
}

/* ============ Side rail ============ */
.ufb-side {
  background: $navy;
  color: #fff;
  padding: 28px;
  display: flex;
  flex-direction: column;
  width: 260px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.ufb-side-top {
  flex: 1;
}

.ufb-coral-bar {
  width: 32px;
  height: 3px;
  background: $coral;
  border-radius: 2px;
  margin-bottom: 10px;
}

.ufb-eyebrow {
  font-family: $font-display;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $coral;
  margin-bottom: 8px;
}

.ufb-side-title {
  font-family: $font-display;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0 0 8px;
  color: #fff;
}

.ufb-side-text {
  font-family: $font-body;
  font-weight: 300;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;
}

.ufb-side-list {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ufb-side-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.85);

  em {
    color: #fff;
    font-style: normal;
    font-weight: 500;
  }
}

.ufb-side-check {
  color: $coral;
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 11px;
}

.ufb-side-foot {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

/* ============ Right pane ============ */
.ufb-main {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ufb-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: $dark-grey;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1), color 120ms;

  &:hover {
    background: $surface-sunken;
    color: $navy;
  }
}

.ufb-body {
  padding: 24px 28px 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ufb-field {
  display: block;
}

.ufb-label {
  font-family: $font-display;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: $dark-grey;
  margin-bottom: 6px;
  display: block;
}

.ufb-required {
  color: $coral;
  margin-left: 2px;
}

/* ---- Category cards ---- */
.ufb-cats {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
}

.ufb-cat {
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid $border-strong;
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: $font-body;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);

  &:hover {
    border-color: $navy;
  }

  &--active {
    border-color: $navy;
    background: $bg-subtle;
  }
}

.ufb-cat-head {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: $font-display;
  font-weight: 600;
  font-size: 13px;
  color: $navy;
}

.ufb-cat-icon {
  font-size: 13px;
  color: $dark-grey;

  &--active {
    color: $coral;
  }
}

.ufb-cat-sub {
  font-size: 11px;
  color: $dark-grey;
  font-weight: 300;
  line-height: 1.3;
}

/* ---- Two-column row ---- */
.ufb-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* ---- Inputs ---- */
.ufb-input,
.ufb-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid $border-strong;
  border-radius: 8px;
  background: #fff;
  font-family: $font-body;
  font-weight: 400;
  font-size: 14px;
  color: $navy;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.2, 0, 0, 1),
    box-shadow 120ms cubic-bezier(0.2, 0, 0, 1);

  &::placeholder {
    color: $fg-3;
    font-weight: 300;
  }

  &:focus {
    border-color: $blue-violet;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
  }
}

.ufb-textarea {
  resize: vertical;
  min-height: 96px;
  line-height: 1.5;
}

/* ---- Drop zone ---- */
.ufb-drop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1.5px dashed $border-strong;
  border-radius: 8px;
  background: $surface-sunken;
  color: $dark-grey;
  font-size: 13px;
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
  margin: 0;

  &:hover,
  &--hover {
    border-color: $navy;
    color: $navy;
    background: $bg-subtle;
  }

  strong {
    font-weight: 500;
    color: $navy;
  }
}

.ufb-drop-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.ufb-drop-clip {
  color: $blue-violet;
  font-size: 13px;
}

.ufb-drop-right {
  font-size: 11px;
  color: $fg-3;
  white-space: nowrap;
}

.ufb-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* ---- Thumbnails ---- */
.ufb-thumbs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: -4px;
}

.ufb-thumb {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid $border;
  background: $bg-subtle;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.ufb-thumb-x {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: $navy;
  color: #fff;
  border: 2px solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 9px;
  box-shadow: 0 1px 2px rgba(18, 40, 63, 0.06);

  &:hover {
    background: $navy-hover;
  }
}

/* ---- Footer ---- */
.ufb-footer {
  margin-top: 18px;
  padding: 14px 28px;
  border-top: 1px solid $divider;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.ufb-btn {
  font-family: $font-display;
  font-weight: 600;
  font-size: 14px;
  padding: 10px 22px;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);

  &--primary {
    background: $navy;
    color: #fff;

    &:hover {
      background: $navy-hover;
    }
  }

  &--ghost {
    background: transparent;
    color: $dark-grey;
    padding: 10px 14px;

    &:hover {
      color: $navy;
    }
  }
}

/* ---- Responsive: stack on narrow screens ---- */
@media (max-width: 640px) {
  .ufb-modal {
    flex-direction: column;
    max-width: 100%;
    border-radius: 0;
  }
  .ufb-side {
    width: 100%;
    padding: 20px;
  }
  .ufb-body {
    padding: 20px 20px 0;
  }
  .ufb-footer {
    padding: 14px 20px;
  }
  .ufb-cats {
    grid-template-columns: 1fr;
  }
  .ufb-row-2 {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
/* Style the b-modal shell so the design owns its own chrome.
   .feed_back-modal is applied to the outer .modal scrim by b-modal's
   class prop (see MainHeader.vue / UTC.vue). */
.feed_back-modal {
  .modal-dialog {
    max-width: 760px;
    margin: 1.75rem auto;
  }

  .modal-content {
    border: none;
    border-radius: 16px;
    background: transparent;
    box-shadow: 0 24px 56px rgba(18, 40, 63, 0.2),
      0 8px 16px rgba(18, 40, 63, 0.08);
    overflow: hidden;
    padding: 0 !important;
  }

  .modal-body {
    padding: 0 !important;
  }
}

/* Dark mode — modal surface */
html.dark .feed_back-modal .modal-content {
  background: transparent;
}
</style>
